import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { TasksContainerComponent } from './containers/tasks-container/tasks-container.component';
import { TaskRequestDetailContainerComponent } from './containers/task-request-detail-container/task-request-detail-container.component';
import { CxpNewCrlPageComponent } from './containers/cxp-new-crl-page/cxp-new-crl-page.component';
import { CxpCrlPanelComponent } from './containers/cxp-crl-panel/cxp-crl-panel.component';
import { DeliverableDetailContainerComponent } from './containers/deliverable-detail-container/deliverable-detail-container.component';
import { EsignDetailContainerComponent } from './containers/esign-detail-container/esign-detail-container.component';
import { EsignSignPageComponent } from './containers/esign-sign-page/esign-sign-page.component';
import { cxpRequestLockdownGuard } from './guards/cxp-request-lockdown.guard';
import { cxpNewCrlPageGuard } from './guards/cxp-new-crl-page.guard';
import {
  CXP_ROUTE_OUTLET_PANEL,
  CXP_ROUTE_PARAM_DELIVERABLE_ID,
  CXP_ROUTE_PARAM_ITEM_ID,
  CXP_ROUTE_PARAM_REQUEST_ID,
  CXP_ROUTE_TOKEN_DELIVERABLE_DETAIL,
  CXP_ROUTE_TOKEN_ESIGN_DETAIL,
  CXP_ROUTE_TOKEN_ESIGN_SIGN,
  CXP_ROUTE_TOKEN_ITEM_DETAIL,
  CXP_ROUTE_TOKEN_REQUEST_DETAIL,
  CXP_ROUTE_TOKEN_TASKS,
} from './models/routing-constants';

// Exported so the flag-gated request-detail wiring can be exercised in a
// routing unit test (which component the shared path resolves to per flag).
export const routes: Routes = [
  {
    path: CXP_ROUTE_TOKEN_TASKS,
    component: TasksContainerComponent,
  },
  // eSign owns a path of its own rather than sharing the request one: an eSign
  // request is a bundle shell with no items, so the CRL detail below has nothing
  // to show for one, and naming the kind in the URL is what lets the router pick
  // the page straight from the link. Signing sits UNDER the eSign detail path, so
  // the signer's URL still names the request they came from and Back returns to
  // its detail page. Both entries carry `:requestId`, which is the param name the
  // route-params service and the lockdown guard key on.
  {
    path: `${CXP_ROUTE_TOKEN_ESIGN_DETAIL}/:${CXP_ROUTE_PARAM_REQUEST_ID}/${CXP_ROUTE_TOKEN_ESIGN_SIGN}`,
    component: EsignSignPageComponent,
    canActivate: [cxpRequestLockdownGuard],
  },
  {
    path: `${CXP_ROUTE_TOKEN_ESIGN_DETAIL}/:${CXP_ROUTE_PARAM_REQUEST_ID}`,
    component: EsignDetailContainerComponent,
    canActivate: [cxpRequestLockdownGuard],
  },
  // Two entries share the request-detail path. The first is the redesigned CXP
  // page, gated by CXP_ENABLE_REQUEST_LIST_REDESIGN via canMatch — when the flag
  // is OFF (or LD is unreachable) the guard returns false, the router skips it,
  // and the legacy entry below matches instead. Both keep the lockdown
  // canActivate guard.
  {
    path: `${CXP_ROUTE_TOKEN_REQUEST_DETAIL}/:${CXP_ROUTE_PARAM_REQUEST_ID}`,
    component: CxpNewCrlPageComponent,
    canMatch: [cxpNewCrlPageGuard],
    canActivate: [cxpRequestLockdownGuard],
    // PP-3753: the per-item side panel is a CHILD rendered into the named
    // `panel` outlet inside the list page, so it sits ALONGSIDE the list on
    // desktop. URL shape: `.../request/:requestId(panel:item/:itemId)`. A direct
    // deep link to that auxiliary URL loads the list page first (parent route),
    // which sets `selectedRequestId` and renders from the raw-list fallback,
    // then activates this child outlet — no special deep-link handling needed.
    children: [
      {
        path: `${CXP_ROUTE_TOKEN_ITEM_DETAIL}/:${CXP_ROUTE_PARAM_ITEM_ID}`,
        outlet: CXP_ROUTE_OUTLET_PANEL,
        component: CxpCrlPanelComponent,
        // MOBILE SEAM (deferred): a future mobile full-page variant can mount
        // this SAME child route (matched by `mobileFullPage`) into the primary
        // outlet instead of `panel`. No behavioral mobile branch is built now;
        // this flag is the documented hook the mobile ticket keys off.
        data: { mobileFullPage: false },
      },
    ],
  },
  // OLD MODULE DEPENDENCY: TaskRequestDetailContainerComponent wraps the legacy
  // ClientRequestPanelComponent. When the redesign flag is fully rolled out,
  // remove this fallback entry and the dependency on ClientRequestsModule.
  {
    path: `${CXP_ROUTE_TOKEN_REQUEST_DETAIL}/:${CXP_ROUTE_PARAM_REQUEST_ID}`,
    component: TaskRequestDetailContainerComponent,
    canActivate: [cxpRequestLockdownGuard],
  },
  {
    path: `${CXP_ROUTE_TOKEN_DELIVERABLE_DETAIL}/:${CXP_ROUTE_PARAM_DELIVERABLE_ID}`,
    component: DeliverableDetailContainerComponent,
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class CxpRoutingModule {}
